import { getAPIHost } from '@rsos/utils/metaTags';

/**
 * Constructs a connect-share URL based on the current environment and share key
 * @param shareKey - The share key for the event share
 * @returns The full connect-share URL with the share key
 * @example
 * // In qa-delta environment:
 * getConnectShareUrl('f5TJteJbzBxzeAMeJUMP0Q')
 * // Returns: 'https://connect-share-delta.qa.rapidsos.com/?share_key=f5TJteJbzBxzeAMeJUMP0Q'
 */
export const getConnectShareUrl = (shareKey: string): string => {
  const apiHost = getAPIHost();

  // Parse the API host to extract environment and namespace
  // Examples:
  // https://api-qa-delta.rapidsos.com -> qa, delta
  // https://api-dev-delta.rapidsos.com -> dev, delta
  // https://api.rapidsos.com -> production, no namespace
  const url = new URL(apiHost);
  const hostParts = url.hostname.split('.');

  // Handle localhost/local development
  if (url.hostname === 'localhost') {
    return `https://connect-share-delta.dev.rapidsos.com/?share_key=${shareKey}`;
  }

  // Extract environment and namespace from hostname
  // api-qa-delta.rapidsos.com -> ['api-qa-delta', 'rapidsos', 'com']
  // api.rapidsos.com -> ['api', 'rapidsos', 'com']
  const firstPart = hostParts[0]; // e.g., 'api-qa-delta' or 'api'

  if (firstPart === 'api') {
    // Production environment
    return `https://connect-share.rapidsos.com/?share_key=${shareKey}`;
  }

  // Split first part by dashes: 'api-qa-delta' -> ['api', 'qa', 'delta']
  const parts = firstPart.split('-');

  if (parts.length === 2) {
    // Format: api-{env} (e.g., api-qa, api-dev)
    const env = parts[1];
    return `https://connect-share.${env}.rapidsos.com/?share_key=${shareKey}`;
  }

  if (parts.length === 3) {
    // Format: api-{env}-{namespace} (e.g., api-qa-delta, api-dev-delta)
    const env = parts[1];
    const namespace = parts[2];
    return `https://connect-share-${namespace}.${env}.rapidsos.com/?share_key=${shareKey}`;
  }

  // Fallback to qa-delta if pattern doesn't match
  return `https://connect-share-delta.qa.rapidsos.com/?share_key=${shareKey}`;
};
